import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { useTodoEvents, SSEEventType } from '../../contexts/SSEContext';
import TodoModal from './TodoModal';

const TodoManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const tripIdFromUrl = searchParams.get('trip');

  const [todos, setTodos] = useState([]);
  const [trips, setTrips] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [filter, setFilter] = useState('all'); // all, trip, standalone
  const [statusFilter, setStatusFilter] = useState('open'); // all, open, completed - default to open
  const [selectedTripIds, setSelectedTripIds] = useState(tripIdFromUrl ? [tripIdFromUrl] : []); // Array of trip IDs to filter by
  const [sortField, setSortField] = useState('due_date'); // Field to sort by
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  const fetchTodos = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/todos`, { headers });
      setTodos(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load todos', e);
      setError('Failed to load todos');
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array - function never changes

  const fetchUsers = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/users`, { headers });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load users', e);
    }
  }, []); // Empty dependency array - function never changes

  const fetchTrips = useCallback(async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const res = await axios.get(`${apiBase}/trips`, { headers });
      setTrips(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load trips', e);
    }
  }, []); // Empty dependency array - function never changes

  useEffect(() => {
    fetchTodos();
    fetchUsers();
    fetchTrips();
  }, [fetchTodos, fetchUsers, fetchTrips]);

  // Handle SSE events for todos (real-time updates)
  const handleTodoEvent = useCallback((event) => {
    const eventType = event.type;
    const todoData = event.data?.todo;

    if (eventType === SSEEventType.TODO_CREATED && todoData) {
      setTodos((prev) => {
        // Don't add if already exists
        if (prev.some((t) => t.id === todoData.id)) return prev;
        return [...prev, todoData];
      });
    } else if (eventType === SSEEventType.TODO_UPDATED && todoData) {
      setTodos((prev) =>
        prev.map((t) => (t.id === todoData.id ? { ...t, ...todoData } : t))
      );
    } else if (eventType === SSEEventType.TODO_DELETED && todoData) {
      setTodos((prev) => prev.filter((t) => t.id !== todoData.id));
    }
  }, []);

  useTodoEvents(handleTodoEvent, []);

  const handleDelete = async (todoId) => {
    if (!window.confirm('Are you sure you want to delete this todo?')) {
      return;
    }

    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.delete(`${apiBase}/todos/${todoId}`, { headers });
      fetchTodos();
    } catch (err) {
      alert('Failed to delete todo: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUpdate = async (todoId, updateData) => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      await axios.put(`${apiBase}/todos/${todoId}`, updateData, { headers });
      fetchTodos();
    } catch (err) {
      alert('Failed to update todo: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleToggleStatus = (todo) => {
    const newStatus = todo.status === 'completed' ? 'open' : 'completed';
    handleUpdate(todo.id, { status: newStatus });
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingTodo(null);
  };

  const handleModalSuccess = () => {
    fetchTodos();
    handleModalClose();
  };

  const handleEdit = (todo) => {
    setEditingTodo(todo);
    setShowModal(true);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter todos based on selected filters and URL query parameter
  const filteredTodos = todos.filter(todo => {
    // If specific trip(s) are selected, only show those todos
    if (selectedTripIds.length > 0) {
      // Convert both to strings for comparison to avoid type mismatches
      const todoTripIdStr = String(todo.trip_id);
      const isMatchingTrip = selectedTripIds.some(id => String(id) === todoTripIdStr);
      if (!isMatchingTrip) return false;
    }

    if (filter === 'trip' && !todo.trip_id) return false;
    if (filter === 'standalone' && todo.trip_id) return false;
    if (statusFilter === 'open' && todo.status !== 'open') return false;
    if (statusFilter === 'completed' && todo.status !== 'completed') return false;
    return true;
  });

  // Sort the filtered todos
  const sortedTodos = [...filteredTodos].sort((a, b) => {
    let aValue, bValue;

    switch (sortField) {
      case 'status':
        aValue = a.status === 'completed' ? 1 : 0;
        bValue = b.status === 'completed' ? 1 : 0;
        break;
      case 'priority':
        const priorityOrder = { high: 3, medium: 2, low: 1 };
        aValue = priorityOrder[a.priority] || 0;
        bValue = priorityOrder[b.priority] || 0;
        break;
      case 'title':
        aValue = (a.title || '').toLowerCase();
        bValue = (b.title || '').toLowerCase();
        break;
      case 'assigned_to':
        aValue = (a.assigned_to_name || '').toLowerCase();
        bValue = (b.assigned_to_name || '').toLowerCase();
        break;
      case 'created_by':
        aValue = (a.created_by_name || '').toLowerCase();
        bValue = (b.created_by_name || '').toLowerCase();
        break;
      case 'trip':
        aValue = (a.trip_title || '').toLowerCase();
        bValue = (b.trip_title || '').toLowerCase();
        break;
      case 'executive':
        aValue = (a.executive_name || '').toLowerCase();
        bValue = (b.executive_name || '').toLowerCase();
        break;
      case 'due_date':
        aValue = a.due_date ? new Date(a.due_date).getTime() : 0;
        bValue = b.due_date ? new Date(b.due_date).getTime() : 0;
        break;
      default:
        return 0;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 'high': return { text: 'High', color: 'bg-red-100 text-red-700' };
      case 'medium': return { text: 'Med', color: 'bg-yellow-100 text-yellow-700' };
      case 'low': return { text: 'Low', color: 'bg-green-100 text-green-700' };
      default: return { text: priority, color: 'bg-gray-100 text-gray-700' };
    }
  };

  // Get trip names for filtered view
  const getSelectedTripNames = () => {
    if (selectedTripIds.length === 0) return null;
    const names = selectedTripIds
      .map(id => {
        const trip = trips.find(t => String(t.id) === String(id));
        return trip?.title || 'Unknown Trip';
      })
      .filter(Boolean);
    return names.length > 0 ? names.join(', ') : null;
  };

  const handleClearTripFilter = () => {
    setSelectedTripIds([]);
    navigate('/todos');
  };

  const handleTripFilterChange = (e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setSelectedTripIds(selected);
  };

  // Get list of active trips (trips with open todos)
  const activeTrips = trips.filter(trip =>
    todos.some(todo => String(todo.trip_id) === String(trip.id) && todo.status !== 'completed')
  );

  return (
    <div className="p-4 sm:p-6 max-w-full mx-auto">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              {selectedTripIds.length > 0 ? `Todos for ${getSelectedTripNames()}` : 'Todo Management'}
            </h2>
            <p className="text-sm text-gray-600">
              {selectedTripIds.length > 0 ? `Showing todos for selected trip${selectedTripIds.length > 1 ? 's' : ''}` : 'Manage all your tasks and assignments'}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {selectedTripIds.length > 0 && (
              <button
                onClick={handleClearTripFilter}
                className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md flex items-center gap-1 sm:gap-2 text-sm"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <span className="hidden sm:inline">Clear Filter</span>
                <span className="sm:hidden">Clear</span>
              </button>
            )}
            <button
              onClick={() => setShowModal(true)}
              className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center gap-1 sm:gap-2 text-sm"
            >
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">Create Todo</span>
              <span className="sm:hidden">Add</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3 sm:gap-4 mb-4">
          <div className="col-span-2 sm:col-span-1">
            <label className="block text-xs font-medium text-gray-700 mb-1">Filter by Trip</label>
            <select
              multiple
              value={selectedTripIds}
              onChange={handleTripFilterChange}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full sm:min-w-[200px]"
              size={Math.min(activeTrips.length + 1, 4)}
            >
              <option value="" disabled className="text-gray-400 italic">
                {activeTrips.length === 0 ? 'No active trips' : 'Select trip(s)...'}
              </option>
              {activeTrips.map(trip => (
                <option key={trip.id} value={trip.id}>
                  {trip.title}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1 hidden sm:block">Hold Ctrl/Cmd to select multiple</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full"
            >
              <option value="all">All</option>
              <option value="standalone">Standalone</option>
              <option value="trip">Trip-Related</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm w-full"
            >
              <option value="all">All</option>
              <option value="open">Open</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-2">Loading todos...</p>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {!loading && sortedTodos.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <p className="text-gray-600 font-medium">No todos found</p>
          <p className="text-gray-500 text-sm mt-1">Create your first todo to get started</p>
        </div>
      )}

      {!loading && sortedTodos.length > 0 && (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-3">
            {sortedTodos.map(todo => {
              const isOverdue = todo.due_date && new Date(todo.due_date) < new Date() && todo.status !== 'completed';
              const priorityBadge = getPriorityBadge(todo.priority);

              return (
                <div
                  key={todo.id}
                  className={`bg-white rounded-xl shadow-sm border border-slate-200 p-4 ${todo.status === 'completed' ? 'opacity-60' : ''}`}
                >
                  {/* Header: Checkbox, Title, Priority */}
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => handleToggleStatus(todo)}
                      className="text-xl hover:scale-110 transition-transform flex-shrink-0 mt-0.5"
                      title={`Mark as ${todo.status === 'completed' ? 'open' : 'completed'}`}
                    >
                      {todo.status === 'completed' ? '✅' : '⬜'}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className={`min-w-0 flex-1 ${todo.status === 'completed' ? 'line-through text-gray-500' : ''}`}>
                          <div className="font-medium text-gray-900 break-words">{todo.title}</div>
                          {todo.description && (
                            <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">{todo.description}</div>
                          )}
                        </div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${priorityBadge.color}`}>
                          {priorityBadge.text}
                        </span>
                      </div>

                      {/* Meta Info */}
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500">
                        {todo.assigned_to_name && (
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {todo.assigned_to_name}
                          </span>
                        )}
                        {todo.due_date && (
                          <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-semibold' : ''}`}>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {new Date(todo.due_date).toLocaleDateString()}
                            {isOverdue && ' (Overdue)'}
                          </span>
                        )}
                        {todo.trip_id && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                            {todo.trip_title || 'Trip'}
                          </span>
                        )}
                        {todo.executive_id && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                            {todo.executive_name || 'Executive'}
                          </span>
                        )}
                        {todo.is_confidential && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800"
                            title="This todo is confidential"
                          >
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            Confidential
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
                    <button
                      onClick={() => handleEdit(todo)}
                      className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(todo.id)}
                      className="px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    onClick={() => handleSort('status')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-10 cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Done
                      {sortField === 'status' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('priority')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-16 cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Priority
                      {sortField === 'priority' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('title')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Title
                      {sortField === 'title' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('assigned_to')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Assigned To
                      {sortField === 'assigned_to' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('trip')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Trip
                      {sortField === 'trip' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('executive')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Executive
                      {sortField === 'executive' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    onClick={() => handleSort('due_date')}
                    className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28 cursor-pointer hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-1">
                      Due Date
                      {sortField === 'due_date' && (
                        <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-20">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedTodos.map(todo => {
                  const isOverdue = todo.due_date && new Date(todo.due_date) < new Date() && todo.status !== 'completed';
                  const priorityBadge = getPriorityBadge(todo.priority);

                  return (
                    <tr key={todo.id} className={`hover:bg-gray-50 ${todo.status === 'completed' ? 'opacity-60' : ''}`}>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleStatus(todo)}
                          className="text-lg hover:scale-110 transition-transform"
                          title={`Mark as ${todo.status === 'completed' ? 'open' : 'completed'}`}
                        >
                          {todo.status === 'completed' ? '✅' : '⬜'}
                        </button>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${priorityBadge.color}`}>
                            {priorityBadge.text}
                          </span>
                          {todo.is_confidential && (
                            <span
                              className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-800"
                              title="This todo is confidential - only visible to creator, assignee, supervisors, and org admins"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className={todo.status === 'completed' ? 'line-through text-gray-500' : ''}>
                          <div className="text-sm font-medium text-gray-900">{todo.title}</div>
                          {todo.description && (
                            <div className="text-xs text-gray-500 mt-0.5 truncate max-w-md" title={todo.description}>
                              {todo.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700">
                        {todo.assigned_to_name || <span className="text-gray-400 italic">Unassigned</span>}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm">
                        {todo.trip_id ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {todo.trip_title || 'Trip'}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm">
                        {todo.executive_id ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            {todo.executive_name || 'Executive'}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-700">
                        {todo.due_date ? (
                          <div className={isOverdue ? 'text-red-600 font-semibold' : ''}>
                            {new Date(todo.due_date).toLocaleDateString()}
                            {isOverdue && <div className="text-xs">Overdue!</div>}
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleEdit(todo)}
                            className="text-gray-400 hover:text-blue-600 p-1"
                            title="Edit todo"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(todo.id)}
                            className="text-gray-400 hover:text-red-600 p-1"
                            title="Delete todo"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showModal && (
        <TodoModal
          todo={editingTodo}
          users={users}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default TodoManagement;
